import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createCategory, updateCategory, getCategories } from '../../Store/categories/categoriesSlice';
import RichTextEditor from './RichTextEditor';
import styles from '../../styles/AdminPanel.module.scss';
import { assetUrl } from '../../utils/imageUrl';
import { getSiteOrigin } from '../../utils/constants';
import { getCategoryIndentLabel, getParentCategoryOptions } from '../../utils/categoryTree';

const SUBCATEGORY_DISPLAY = [
  { value: 'list', label: 'Список' },
  { value: 'grid', label: 'Сітка' },
  { value: 'hidden', label: 'Приховати подкатегории' },
];

const emptyForm = () => ({
  name: '',
  nameRu: '',
  parentId: '',
  descriptionBefore: '',
  descriptionBeforeRu: '',
  descriptionAfter: '',
  descriptionAfterRu: '',
  isPublished: true,
  h1Title: '',
  canonical: '',
  metaRobots: '',
  seoIndex: true,
  seoFollow: true,
  subcategoryDisplay: 'list',
  metaTitle: '',
  metaTitleRu: '',
  metaDescription: '',
  metaDescriptionRu: '',
  metaKeywords: '',
  image: null,
});

const stripHtml = (html) => {
  if (!html) return '';
  return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const SeoPreview = ({ title, url, description }) => (
  <div className={styles.categorySeoPreview}>
    <div className={styles.categorySeoPreviewUrl}>{url}</div>
    <div className={styles.categorySeoPreviewTitle}>{title || 'Заголовок сторінки'}</div>
    <div className={styles.categorySeoPreviewDesc}>
      {description || "Опис для пошукових систем з'явиться тут."}
    </div>
  </div>
);

const CategoryForm = ({ category, categories = [], onCancel, onSuccess, onDelete, showMessage }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(emptyForm());

  const parentOptions = useMemo(
    () => getParentCategoryOptions(categories, category?.id),
    [categories, category?.id]
  );

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        nameRu: category.nameRu || '',
        parentId: category.parentId ? String(category.parentId) : '',
        descriptionBefore: category.descriptionBefore || '',
        descriptionBeforeRu: category.descriptionBeforeRu || '',
        descriptionAfter: category.descriptionAfter || '',
        descriptionAfterRu: category.descriptionAfterRu || '',
        isPublished: category.isPublished !== false,
        h1Title: category.h1Title || '',
        canonical: category.canonical || '',
        metaRobots: category.metaRobots || '',
        seoIndex: category.seoIndex !== false,
        seoFollow: category.seoFollow !== false,
        subcategoryDisplay: category.subcategoryDisplay || 'list',
        metaTitle: category.metaTitle || '',
        metaTitleRu: category.metaTitleRu || '',
        metaDescription: category.metaDescription || '',
        metaDescriptionRu: category.metaDescriptionRu || '',
        metaKeywords: category.metaKeywords || '',
        image: null,
      });
    } else {
      setFormData(emptyForm());
    }
  }, [category]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
    }
    e.target.value = '';
  };

  const buildFormData = () => {
    const data = new FormData();
    data.append('name', formData.name);
    data.append('nameRu', formData.nameRu);
    data.append('parentId', formData.parentId);
    data.append('descriptionBefore', formData.descriptionBefore);
    data.append('descriptionBeforeRu', formData.descriptionBeforeRu);
    data.append('descriptionAfter', formData.descriptionAfter);
    data.append('descriptionAfterRu', formData.descriptionAfterRu);
    data.append('isPublished', formData.isPublished ? '1' : '0');
    data.append('h1Title', formData.h1Title);
    data.append('canonical', formData.canonical);
    data.append('metaRobots', formData.metaRobots);
    data.append('seoIndex', formData.seoIndex ? '1' : '0');
    data.append('seoFollow', formData.seoFollow ? '1' : '0');
    data.append('subcategoryDisplay', formData.subcategoryDisplay);
    data.append('metaTitle', formData.metaTitle);
    data.append('metaTitleRu', formData.metaTitleRu);
    data.append('metaDescription', formData.metaDescription);
    data.append('metaDescriptionRu', formData.metaDescriptionRu);
    data.append('metaKeywords', formData.metaKeywords);
    if (formData.image) {
      data.append('image', formData.image);
    }
    return data;
  };

  const validate = () => {
    if (!formData.name.trim()) {
      showMessage('error', 'Заповніть назву групи (українською)');
      return false;
    }
    if (!category && !formData.image) {
      showMessage('error', 'Завантажте зображення групи');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showMessage('', '');
    if (!validate()) return;

    try {
      const data = buildFormData();
      if (category) {
        await dispatch(updateCategory({ id: category.id, formData: data })).unwrap();
        showMessage('success', 'Групу успішно збережено!');
      } else {
        await dispatch(createCategory(data)).unwrap();
        showMessage('success', 'Групу успішно створено!');
      }
      dispatch(getCategories());
      onSuccess();
    } catch (error) {
      showMessage('error', 'Помилка при збереженні групи');
      console.error(error);
    }
  };

  const previewImage = formData.image
    ? URL.createObjectURL(formData.image)
    : category?.image
      ? assetUrl(category.image)
      : null;

  const seoTitleUk = formData.metaTitle || formData.name;
  const seoTitleRu = formData.metaTitleRu || formData.nameRu || formData.name;
  const seoDescUk = formData.metaDescription || stripHtml(formData.descriptionBefore) || formData.name;
  const seoDescRu = formData.metaDescriptionRu || stripHtml(formData.descriptionBeforeRu) || formData.nameRu || formData.name;
  const previewUrl = formData.canonical || `${getSiteOrigin()}/categories/${category?.id || 'new'}`;

  return (
    <div className={styles.categoryFormCard}>
      <form onSubmit={handleSubmit}>
        <div className={styles.categoryFormHeader}>
          <h2>{category ? 'Редагування групи' : 'Створення групи'}</h2>
          <div className={styles.categoryFormHeaderActions}>
            <button type="button" className={styles.productFormCancelBtn} onClick={onCancel}>
              Скасувати
            </button>
            <button type="submit" className={styles.productFormSaveBtn}>
              Зберегти группу
            </button>
          </div>
        </div>

        <div className={styles.categoryFormBody}>
          <div className={styles.categoryFormMain}>
            <section className={styles.categoryFormSection}>
              <h3 className={styles.categoryFormSectionTitle}>Основна інформація</h3>
              <div className={styles.categoryFormSectionContent}>
                <div className={styles.categoryFormLangGrid}>
                  <div className={styles.categoryFormLangCol}>
                    <span className={styles.categoryFormLangLabel}>Українська</span>
                    <div className={styles.productFormField}>
                      <label htmlFor="categoryNameUk">Назва групи *</label>
                      <input
                        id="categoryNameUk"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className={styles.productFormField}>
                      <label>Батьківська група</label>
                      <select
                        value={formData.parentId}
                        onChange={(e) => setFormData((p) => ({ ...p, parentId: e.target.value }))}
                      >
                        <option value="">Немає батьківської групи</option>
                        {parentOptions.map((c) => (
                          <option key={c.id} value={c.id}>
                            {getCategoryIndentLabel(c.name, c.depth)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.productFormField}>
                      <label>Опис (до списка товаров)</label>
                      <RichTextEditor
                        editorKey={category?.id ? `cat-before-uk-${category.id}` : 'cat-before-uk-new'}
                        value={formData.descriptionBefore}
                        onChange={(v) => setFormData((p) => ({ ...p, descriptionBefore: v }))}
                        placeholder="Текст перед товарами"
                      />
                    </div>
                    <div className={styles.productFormField}>
                      <label>Опис (после списка товаров)</label>
                      <RichTextEditor
                        editorKey={category?.id ? `cat-after-uk-${category.id}` : 'cat-after-uk-new'}
                        value={formData.descriptionAfter}
                        onChange={(v) => setFormData((p) => ({ ...p, descriptionAfter: v }))}
                        placeholder="Текст после товаров"
                      />
                    </div>
                  </div>

                  <div className={styles.categoryFormLangCol}>
                    <span className={styles.categoryFormLangLabel}>Російська</span>
                    <div className={styles.productFormField}>
                      <label htmlFor="categoryNameRu">Назва групи</label>
                      <input
                        id="categoryNameRu"
                        type="text"
                        value={formData.nameRu}
                        onChange={(e) => setFormData((p) => ({ ...p, nameRu: e.target.value }))}
                      />
                    </div>
                    <div className={styles.productFormField}>
                      <label>Батьківська група</label>
                      <select
                        value={formData.parentId}
                        onChange={(e) => setFormData((p) => ({ ...p, parentId: e.target.value }))}
                      >
                        <option value="">Немає батьківської групи</option>
                        {parentOptions.map((c) => (
                          <option key={c.id} value={c.id}>
                            {getCategoryIndentLabel(c.nameRu || c.name, c.depth)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.productFormField}>
                      <label>Опис (до списка товаров)</label>
                      <RichTextEditor
                        editorKey={category?.id ? `cat-before-ru-${category.id}` : 'cat-before-ru-new'}
                        value={formData.descriptionBeforeRu}
                        onChange={(v) => setFormData((p) => ({ ...p, descriptionBeforeRu: v }))}
                        placeholder="Текст перед товарами"
                      />
                    </div>
                    <div className={styles.productFormField}>
                      <label>Опис (после списка товаров)</label>
                      <RichTextEditor
                        editorKey={category?.id ? `cat-after-ru-${category.id}` : 'cat-after-ru-new'}
                        value={formData.descriptionAfterRu}
                        onChange={(v) => setFormData((p) => ({ ...p, descriptionAfterRu: v }))}
                        placeholder="Текст после товаров"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className={styles.categoryFormSection}>
              <h3 className={styles.categoryFormSectionTitle}>SEO</h3>
              <div className={styles.categoryFormSectionContent}>
                <div className={styles.categoryFormLangGrid}>
                  <div className={styles.categoryFormLangCol}>
                    <span className={styles.categoryFormLangLabel}>Українська</span>
                    <div className={styles.productFormField}>
                      <label>Meta title</label>
                      <input
                        type="text"
                        value={formData.metaTitle}
                        onChange={(e) => setFormData((p) => ({ ...p, metaTitle: e.target.value }))}
                        maxLength={200}
                      />
                      <small>{formData.metaTitle.length}/200</small>
                    </div>
                    <div className={styles.productFormField}>
                      <label>Meta description</label>
                      <textarea
                        rows={3}
                        value={formData.metaDescription}
                        onChange={(e) => setFormData((p) => ({ ...p, metaDescription: e.target.value }))}
                        maxLength={200}
                      />
                      <small>{formData.metaDescription.length}/200</small>
                    </div>
                    <SeoPreview title={seoTitleUk} url={previewUrl} description={seoDescUk} />
                  </div>

                  <div className={styles.categoryFormLangCol}>
                    <span className={styles.categoryFormLangLabel}>Російська</span>
                    <div className={styles.productFormField}>
                      <label>Meta title</label>
                      <input
                        type="text"
                        value={formData.metaTitleRu}
                        onChange={(e) => setFormData((p) => ({ ...p, metaTitleRu: e.target.value }))}
                        maxLength={200}
                      />
                      <small>{formData.metaTitleRu.length}/200</small>
                    </div>
                    <div className={styles.productFormField}>
                      <label>Meta description</label>
                      <textarea
                        rows={3}
                        value={formData.metaDescriptionRu}
                        onChange={(e) => setFormData((p) => ({ ...p, metaDescriptionRu: e.target.value }))}
                        maxLength={200}
                      />
                      <small>{formData.metaDescriptionRu.length}/200</small>
                    </div>
                    <SeoPreview title={seoTitleRu} url={previewUrl} description={seoDescRu} />
                  </div>
                </div>
                <div className={styles.productFormField}>
                  <label>Meta keywords</label>
                  <input
                    type="text"
                    value={formData.metaKeywords}
                    onChange={(e) => setFormData((p) => ({ ...p, metaKeywords: e.target.value }))}
                  />
                </div>
              </div>
            </section>
          </div>

          <aside className={styles.categoryFormSidebar}>
            <div className={styles.categoryFormSidebarBlock}>
              <h4>Видимість</h4>
              <label className={styles.categoryFormRadio}>
                <input
                  type="radio"
                  name="visibility"
                  checked={formData.isPublished}
                  onChange={() => setFormData((p) => ({ ...p, isPublished: true }))}
                />
                Опубліковано
              </label>
              <label className={styles.categoryFormRadio}>
                <input
                  type="radio"
                  name="visibility"
                  checked={!formData.isPublished}
                  onChange={() => setFormData((p) => ({ ...p, isPublished: false }))}
                />
                Приховано
              </label>
            </div>

            <div className={styles.categoryFormSidebarBlock}>
              <h4>Зображення групи</h4>
              <div className={styles.categoryFormImagePreview}>
                {previewImage ? (
                  <img src={previewImage} alt="Категория" />
                ) : (
                  <span>Немає зображення</span>
                )}
              </div>
              <label className={styles.productFormUploadBtn}>
                {formData.image || category ? 'Замінити изображение' : 'Завантажити зображення'}
                <input type="file" accept="image/*" onChange={handleImageChange} />
              </label>
            </div>

            <div className={styles.categoryFormSidebarBlock}>
              <h4>Тип виводу підкатегорії</h4>
              <select
                className={styles.categoryFormSidebarSelect}
                value={formData.subcategoryDisplay}
                onChange={(e) => setFormData((p) => ({ ...p, subcategoryDisplay: e.target.value }))}
              >
                {SUBCATEGORY_DISPLAY.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </aside>
        </div>

        <div className={styles.categoryFormFooter}>
          <button type="submit" className={styles.productFormSaveBtn}>
            Зберегти группу
          </button>
          {category && onDelete && (
            <button
              type="button"
              className={styles.categoryFormDeleteBtn}
              onClick={() => onDelete(category)}
            >
              Видалити группу
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;
